"use client";

import { useEffect } from "react";

/**
 * What `document.title` reads back after `title` is assigned: browsers collapse
 * runs of ASCII whitespace and trim the ends. Comparing against the raw string
 * would never match for a title with a double space, and the observer below
 * would rewrite it forever and freeze the tab.
 */
export function asDocumentTitle(title: string): string {
  return title.replace(/[\t\n\f\r ]+/g, " ").replace(/^ | $/g, "");
}

/**
 * Sets the tab title and the description meta tag for the active UI locale.
 * The server always renders the English metadata (the locale lives in
 * localStorage) and Next re-inserts it after hydration, so the values are
 * re-applied whenever <head> changes for as long as the page is mounted.
 */
export function usePageMeta(title?: string, description?: string) {
  useEffect(() => {
    if (title === undefined || description === undefined) return;
    const expectedTitle = asDocumentTitle(title);
    const apply = () => {
      if (document.title !== expectedTitle) document.title = title;
      const tags = document.querySelectorAll<HTMLMetaElement>('meta[name="description"]');
      if (tags.length === 0) {
        const tag = document.createElement("meta");
        tag.name = "description";
        tag.content = description;
        document.head.appendChild(tag);
        return;
      }
      tags.forEach((tag) => {
        if (tag.content !== description) tag.content = description;
      });
    };
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(document.head, { childList: true, subtree: true, characterData: true, attributes: true });
    return () => observer.disconnect();
  }, [title, description]);
}
